import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Header from "../components/Header";
import referral from "../assets/img/HomePage/referral.svg";
import ModalAbout from "../components/ModalAbout";
import { makeStyles } from "@mui/styles";
import { Typography } from "@mui/material";
import Options from "../components/Options";
import organization from "../assets/img/Referral/organization.svg";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { changeTab } from "../redux/TabSlice";
import { setSelectedItem } from "../redux/OrganizationSlice";
import {
  formatPhoneForTel,
  getOrgContactList,
  getOrgEmailList,
  getTitleFromAppContent,
  getTranslation,
  normalizeWebsiteUrl,
} from "../helpers/utils";
import { trackOrganizationView, trackPageView } from "../helpers/api";
import callLeft from "../assets/img/EmergencyServices/callLeft.svg";
import callRight from "../assets/img/EmergencyServices/callRight.svg";
import PageBackButton from "../components/PageBackButton";
import { LocationPinIcon } from "../components/LocationPinIcon";
import { colors } from "../constants/theme";

const useStyles = makeStyles((theme) => ({
  bgcolor: {
    background: "#FFFFFF",
  },
  textColor: {
    color: theme.palette.referral.main,
  },
  input: {
    "&::placeholder": {
      color: theme.palette.referral.main,
    },
  },

  sectionbg: {
    background: theme.palette.primary.main,
  },

  sectionBgLight: {
    backgroundColor: "#FCEFF7",
  },

  referralBg: {
    backgroundColor: theme.palette.referral.light,
  },

  emergencyTextBg: {
    backgroundColor: theme.palette.referral.main,
  },

  fullScreen: {
    minHeight: "100vh",
  },
  height: {
    maxHeight: "50vh",
  },
}));

function HelplineDetails() {
  const classes = useStyles();
  const { isEnglish, selLanguageId } = useSelector((state) => state.language);
  const { selDistrictId } = useSelector((state) => state.district);
  const { selProvinceId } = useSelector((state) => state.province);
  const [openAbout, setOpenAbout] = useState(false);
  const [, setOpenItem] = useState(false);
  const { appContent, selectedProvider, selectedProviderEng } = useSelector(
    (state) => state.appContent
  );
  const handleOpen = () => setOpenAbout(true);
  const { selectedItem } = useSelector((state) => state.organizations);
  const { backToGuide } = useSelector((state) => state.page);
  const [referralTitle, setReferralTitle] = useState("");
  const [description, setDescription] = React.useState();
  const [hours, setHours] = React.useState();
  const handleClose = () => setOpenAbout(false);

  useEffect(() => {
    trackPageView("Helpline Details", { province: selProvinceId, district: selDistrictId, language: selLanguageId });
  }, [selProvinceId, selDistrictId, selLanguageId]);

  const [tabSelect, setTabSelect] = useState("HelplineDetails");
  const dispatch = useDispatch();

  useEffect(() => {
    if (appContent) {
      setReferralTitle(getTitleFromAppContent(appContent, "Support Providing Organizations"));
    }
  }, [appContent]);

  useEffect(() => {
    dispatch(changeTab(tabSelect));
  }, [tabSelect, dispatch]);

  // Fetch full org once per id (search returns limited fields; referral list may have pre-translated contact)
  useEffect(() => {
    const orgId = selectedItem?._id != null ? String(selectedItem._id) : null;
    if (!orgId) return;
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_LOCAL_API}/organization/${orgId}`)
      .then((res) => {
        if (cancelled) return;
        const full = res.data?.data;
        if (full) dispatch(setSelectedItem(full));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedItem?._id, dispatch]);

  const contactList = useMemo(
    () => getOrgContactList(selectedItem?.contact, selLanguageId),
    [selectedItem?.contact, selLanguageId]
  );

  const emailList = useMemo(
    () => getOrgEmailList(selectedItem?.email),
    [selectedItem?.email]
  );

  const websiteUrl = useMemo(
    () => normalizeWebsiteUrl(selectedItem?.website_link),
    [selectedItem?.website_link]
  );

  // Track organization view for admin dashboard
  useEffect(() => {
    if (selectedItem?._id) {
      trackOrganizationView(selectedItem._id, {
        province: selProvinceId,
        district: selDistrictId,
        language: selLanguageId,
      });
    }
  }, [selectedItem?._id, selProvinceId, selDistrictId, selLanguageId]);

  // Helper to safely extract text from Translation objects (memoized for useEffect deps)
  const getTextFromTranslation = useCallback((translation) => {
    if (!translation) return '';
    if (typeof translation === 'string') return translation;
    if (Array.isArray(translation)) {
      const result = getTranslation(translation, selLanguageId);
      if (result && typeof result === 'string') return result;
      if (translation.length > 0 && translation[0]?.text && typeof translation[0].text === 'string') {
        return translation[0].text;
      }
      return '';
    }
    if (typeof translation === 'object' && translation.text) {
      return typeof translation.text === 'string' ? translation.text : '';
    }
    return '';
  }, [selLanguageId]);

  React.useEffect(() => {
    const createDes = (str) => {
      if (!str) return;

      const textStr = typeof str === 'string' ? str : getTextFromTranslation(str);
      if (!textStr || typeof textStr !== "string") return;

      const parts = textStr.split(":");
      setDescription(parts[0]);
      setHours(parts[1]);
    };
    createDes(selectedItem?.description);
  }, [selectedItem, selLanguageId, getTextFromTranslation]);

  const openMap = (address) => {
    const destination = encodeURIComponent(`${address}`);
    const link = `http://maps.google.com/?daddr=${destination}`;
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  if (!selectedItem) return;

  const handleBack = () => {
    if (backToGuide) setTabSelect("GuideStep");
    else setTabSelect(selectedProviderEng);
  };

  return (
    <div className={`${classes.sectionBgLight} pb-20 flex flex-col`}>
      <div>
        <Header />
        <PageBackButton onClick={handleBack} />
        <div className="flex-col md:flex-row flex justify-between">
          <div className=" block md:hidden mb-5">
            <Options handleOpen={handleOpen} />
          </div>
          <div className="flex mx-5 mt-2 content-center items-center">
            <img className="w-6 mr-3 mb-2" src={referral} alt="Emergency" />
            <div className="flex flex-col md:flex-row">
              <Typography
                onClick={() => setTabSelect("ReferralDirectory")}
                className="mr-3 cursor-pointer"
                color={"black"}
                variant="p"
              >
                {referralTitle} {">"}
              </Typography>
              <Typography
                onClick={() => setTabSelect(selectedProviderEng)}
                className="mr-3 cursor-pointer"
                color={"black"}
                variant="p"
              >
                {selectedProvider} {">"}
              </Typography>
              <Typography className="font-bold" color={"black"} variant="p">
                {(() => {
                  if (!selectedItem?.title) return '';
                  if (typeof selectedItem.title === 'string') return selectedItem.title;
                  return getTextFromTranslation(selectedItem.title);
                })()}
              </Typography>
            </div>
          </div>
          <div className=" hidden md:block">
            <Options handleOpen={handleOpen} />
          </div>
        </div>

        <div className="flex flex-col justify-center content-center items-center my-2">
          <div
            className={clsx(
              "flex-col lg:flex-row flex px-10 my-5 gap-4 w-full max-w-6xl justify-center",
              "max-h-[min(85dvh,880px)] overflow-y-auto overscroll-contain"
            )}
          >
            <div
              className={clsx(
                "bg-white w-full max-w-sm md:max-w-md p-5 lg:m-3 m-0 rounded-xl shadow-sm border-l-4"
              )}
              style={{ borderLeftColor: colors.primaryDark }}
            >
              <Typography
                className={`block ${isEnglish ? "text-left" : "text-right"} text-gray-800`}
                variant="p"
                style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}
              >
                {description}
              </Typography>
              <Typography
                className={`block mt-4 ${
                  isEnglish ? "text-left" : "text-right"
                } font-bold`}
                variant="p"
                style={{ color: colors.primaryDark }}
              >
                {hours}
              </Typography>
            </div>
            <div>
              <div
                className={clsx(
                  "p-3 w-full max-w-sm md:max-w-md my-3 rounded-xl flex justify-center items-center bg-white shadow-sm border-l-4",
                  isEnglish ? "flex-row" : "flex-row-reverse"
                )}
                style={{ borderLeftColor: colors.primaryDark }}
              >
                <img className="w-9 mx-2" src={organization} alt="Map" />
                <Typography 
                  variant="p" 
                  className="mt-2 font-bold"
                  style={{ color: colors.primaryDark }}
                >
                  {getTextFromTranslation(selectedItem?.organizational_status)}
                </Typography>
              </div>
              <div
                className={clsx("p-3 rounded-xl bg-white shadow-sm")}
                onClick={() => setOpenItem(true)}
              >
                {contactList.map((number, index) => {
                  const tel = formatPhoneForTel(number);
                  return (
                    <a
                      key={`${number}-${index}`}
                      href={tel ? `tel:${tel}` : undefined}
                      className={clsx(
                        "flex p-3 rounded-xl items-center my-2 border border-gray-200 no-underline",
                        isEnglish ? "flex-row" : "flex-row-reverse",
                        tel ? "cursor-pointer hover:bg-gray-50" : "pointer-events-none"
                      )}
                    >
                      <div
                        className="p-2 rounded-full"
                        style={{ backgroundColor: colors.primary }}
                      >
                        <img
                          className="w-5 h-5"
                          src={isEnglish ? callLeft : callRight}
                          alt="call"
                        />
                      </div>
                      <Typography
                        variant="p"
                        className="mx-3"
                        style={{ color: colors.primaryDark }}
                      >
                        {number}
                      </Typography>
                    </a>
                  );
                })}
                {emailList.map((emailVal, index) => (
                  <a
                    key={`email-${emailVal}-${index}`}
                    href={`mailto:${emailVal}`}
                    className={clsx(
                      "flex p-3 rounded-xl items-center my-2 border border-gray-200 no-underline cursor-pointer hover:bg-gray-50",
                      isEnglish ? "flex-row" : "flex-row-reverse"
                    )}
                  >
                    <Typography
                      variant="p"
                      className="mx-3"
                      style={{ color: colors.primaryDark }}
                    >
                      {emailVal}
                    </Typography>
                  </a>
                ))}
                {websiteUrl ? (
                  <a
                    href={websiteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={clsx(
                      "flex p-3 rounded-xl items-center my-2 border border-gray-200 no-underline cursor-pointer hover:bg-gray-50",
                      isEnglish ? "flex-row" : "flex-row-reverse"
                    )}
                  >
                    <Typography
                      variant="p"
                      className="mx-3 break-all"
                      style={{ color: colors.primaryDark }}
                    >
                      {selectedItem.website_link}
                    </Typography>
                  </a>
                ) : null}
                {(() => {
                  const rawAddr = selectedItem?.address;
                  const addressArray = Array.isArray(rawAddr) ? rawAddr : rawAddr != null && rawAddr !== "" ? [rawAddr] : [];
                  const firstAddr = addressArray[0];
                  const addrText = firstAddr == null ? "" : typeof firstAddr === "string" ? firstAddr : getTextFromTranslation(firstAddr);
                  if (addressArray.length === 0 || !addrText) return null;
                  return (
                    <div
                      className={clsx(
                        "flex justify-between items-center my-3 p-3 rounded-xl border border-gray-200",
                        isEnglish ? "flex-row" : "flex-row-reverse"
                      )}
                    >
                      <div className="flex flex-col w-full max-w-xs md:max-w-sm">
                        <Typography
                          variant="p"
                          className={isEnglish ? "text-left" : "text-right"}
                          style={{ color: colors.primaryDark }}
                        >
                          {addrText}
                        </Typography>
                      </div>
                      <button
                        type="button"
                        onClick={() => openMap(addrText)}
                        className="flex shrink-0 p-0 border-0 bg-transparent cursor-pointer"
                        aria-label="Open address in maps"
                      >
                        <LocationPinIcon size={28} color={colors.primaryDark} />
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <ModalAbout open={openAbout} handleClose={handleClose} />
      </div>
    </div>
  );
}

export default HelplineDetails;
